import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CalculateIcon from "@mui/icons-material/Calculate";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionIcon from "@mui/icons-material/Description";
import DialpadIcon from "@mui/icons-material/Dialpad";
import SensorsIcon from "@mui/icons-material/Sensors";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import LanguageIcon from "@mui/icons-material/Language";
import AppsIcon from "@mui/icons-material/Apps";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import DiscountIcon from "@mui/icons-material/Discount";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PsychologyIcon from "@mui/icons-material/Psychology";
import {
  AskQuestionIcon,
  CoinsIcon,
  ForwardArrowIcon,
  FriendsIcon,
  GmailLogoIcon,
  HomeIcon,
  InstagramVerificationBadgeIcon,
  LogoutRoundedIcon,
  MerchantAccountIcon,
  MoreIcon,
  NotificationIcon,
  SelectUsersIcon,
  SortingArrowsIcon,
  UserIcon,
} from "Components/Shared/Icons";
import { getHunterLinkTarget } from "Components/Hunter/openHunter";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { useNavigate } from "Navigation/useNavigate";
import { resetUser } from "State/Auth/AuthReducer";
import { Resources, useResource } from "Translations/Resources";
import type { ApplicationLink } from "Types/types";
import { appUrl } from "Utils/UrlUtils";
import { useDispatch } from "react-redux";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

const RESOURCES = Resources.Pages.Titles;

export const useMenuLinks = () => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const topLinks = {
    overview: {
      text: t(RESOURCES.Dashboard),
      url: appUrl("dashboard"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <HomeIcon />,
    },
    merchantPayment: {
      text: t(RESOURCES.MerchantOverview),
      url: appUrl("merchant"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <ForwardArrowIcon />,
    },
    merchantKeypadPayment: {
      text: t(RESOURCES.NewPayment),
      url: appUrl("initiate-keypad-payment"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <DialpadIcon />,
    },
    paypoints: {
      text: t(RESOURCES.PayPoints),
      url: appUrl("paypoints"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <SensorsIcon />,
    },
    stories: {
      text: t(Resources.Stories.Management.Title),
      url: appUrl("admin-stories"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <AutoStoriesIcon />,
    },
    employeeKeypadPayment: {
      text: t(RESOURCES.NewPayment),
      url: appUrl("employee-initiate-keypad-payment"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <DialpadIcon />,
    },
    feeCalculator: {
      text: t(RESOURCES.FeeCalculator),
      url: appUrl("merchant-fee-calculator"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <CalculateIcon />,
    },
    settlements: {
      text: t(RESOURCES.Settlement),
      url: appUrl("settlements"),
      accessRightCode: AccessRightCodes.Settlements,
      icon: <CurrencyExchangeIcon />,
    },
    merchantSettlements: {
      text: t(RESOURCES.Settlement),
      url: appUrl("merchant-settlements"),
      accessRightCode: AccessRightCodes.Settlements,
      icon: <CurrencyExchangeIcon />,
    },
    transactions: {
      text: t(RESOURCES.TransactionsHistory),
      url: appUrl("transactions"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <SortingArrowsIcon />,
    },
    bankAccounts: {
      text: t(RESOURCES.BankAccounts),
      url: appUrl("bank-address-list"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <MerchantAccountIcon />,
    },
    licenses: {
      text: t(RESOURCES.Licenses),
      url: appUrl("licenses"),
      accessRightCode: AccessRightCodes.Licenses,
      icon: <InstagramVerificationBadgeIcon />,
    },
    merchants: {
      text: t(RESOURCES.Merchants),
      url: appUrl("merchants"),
      accessRightCode: AccessRightCodes.Merchants,
      icon: <CoinsIcon />,
    },
    allMerchants: {
      text: t(RESOURCES.AllMerchants),
      url: appUrl("all-merchants"),
      accessRightCode: AccessRightCodes.Merchants,
      icon: <StorefrontIcon />,
    },
    unassignedMerchants: {
      text: t(RESOURCES.UnassignedMerchants),
      url: appUrl("unassigned-merchants"),
      accessRightCode: AccessRightCodes.Merchants,
      icon: <StorefrontIcon />,
    },
    hunters: {
      text: t(RESOURCES.Hunters),
      url: appUrl("hunters"),
      accessRightCode: AccessRightCodes.Hunters,
      icon: <SelectUsersIcon />,
    },
    hunterPortal: {
      text: t(RESOURCES.HunterPortal),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <PsychologyIcon />,
      ...getHunterLinkTarget(),
    },
    settings: {
      text: t(RESOURCES.Settings),
      url: appUrl("settings"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <MoreIcon />,
    },
    users: {
      text: t(RESOURCES.Users),
      url: appUrl("user-list"),
      accessRightCode: AccessRightCodes.Users,
      icon: <SelectUsersIcon />,
    },
    amlUsers: {
      text: t(RESOURCES.Users),
      url: appUrl("aml-users"),
      accessRightCode: AccessRightCodes.Users,
      icon: <SelectUsersIcon />,
    },
    toBeVerified: {
      text: t(RESOURCES.ToBeVerified),
      url: appUrl("to-be-verified"),
      accessRightCode: AccessRightCodes.Users,
      icon: <FactCheckIcon />,
    },
    legalDocuments: {
      text: t(RESOURCES.LegalDocuments),
      url: appUrl("legal-documents"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <DescriptionIcon />,
    },
    employees: {
      text: t(RESOURCES.Employees),
      url: appUrl("employees"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <PeopleAltOutlinedIcon />,
    },
    stardust: {
      text: t(RESOURCES.Stardust),
      url: appUrl("stardust"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <AutoAwesomeIcon />,
    },
    people: {
      text: t(RESOURCES.People),
      url: appUrl("people"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <PeopleAltOutlinedIcon />,
    },
    // The business personas' social page (FollowersPage): a Merchant's followers, no mutuals or
    // follows.
    followers: {
      text: t(RESOURCES.Followers),
      url: appUrl("followers"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <PeopleAltOutlinedIcon />,
    },
    // The same page for Hunters, who can't be followed (Rules v2, K3): people search only, so it
    // is labelled for what it holds.
    peopleSearch: {
      text: t(RESOURCES.People),
      url: appUrl("followers"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <PeopleAltOutlinedIcon />,
    },
    thanksToYou: {
      text: t(RESOURCES.ThanksToYou),
      url: appUrl("affiliate"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <FriendsIcon />,
    },
    valor: {
      text: t(RESOURCES.Valor),
      url: appUrl("valor"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <MilitaryTechIcon />,
    },
    notificationsManagement: {
      text: t(RESOURCES.NotificationsManagement),
      url: appUrl("notifications-management"),
      accessRightCode: AccessRightCodes.Notifications,
      icon: <NotificationIcon />,
    },
    whereToPay: {
      text: t(RESOURCES.WhereToPay),
      url: appUrl("where-to-pay"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <StorefrontIcon />,
    },
    campaigns: {
      text: t(RESOURCES.Campaigns),
      url: appUrl("admin-campaigns"),
      accessRightCode: AccessRightCodes.Campaigns,
      icon: <LocalOfferIcon />,
    },
    coupons: {
      text: t(RESOURCES.Coupons),
      url: appUrl("admin-coupons"),
      accessRightCode: AccessRightCodes.Coupons,
      icon: <DiscountIcon />,
    },
    ssoClients: {
      text: t(RESOURCES.SsoClients),
      url: appUrl("admin-sso-clients"),
      accessRightCode: AccessRightCodes.SystemControl,
      icon: <VpnKeyIcon />,
    },
    terminals: {
      text: t(RESOURCES.Terminals),
      url: appUrl("admin-terminals"),
      accessRightCode: AccessRightCodes.TerminalInventory,
      icon: <PointOfSaleIcon />,
    },
    raffle: {
      text: t(RESOURCES.Raffle),
      url: appUrl("admin-raffle"),
      accessRightCode: AccessRightCodes.Raffle,
      icon: <ConfirmationNumberIcon />,
    },
    // Customer page under the same right (Customers hold Raffle Read).
    prizeDraw: {
      text: t(RESOURCES.Raffle),
      url: appUrl("raffle"),
      accessRightCode: AccessRightCodes.Raffle,
      icon: <ConfirmationNumberIcon />,
    },
    rolesPermissions: {
      text: t(RESOURCES.RolesPermissions),
      url: appUrl("roles-permissions"),
      accessRightCode: AccessRightCodes.Permissions,
      icon: <AdminPanelSettingsIcon />,
    },
    chat: {
      text: t(RESOURCES.Chat),
      url: appUrl("chat"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <ChatBubbleOutlineIcon />,
    },
    batchPayments: {
      text: t(RESOURCES.BatchPayments),
      url: appUrl("batch-payments"),
      accessRightCode: AccessRightCodes.Banking,
      icon: <CurrencyExchangeIcon />,
    },
    systemControl: {
      text: t(RESOURCES.SystemControl),
      url: appUrl("system-control"),
      accessRightCode: AccessRightCodes.SystemControl,
      icon: <SettingsIcon />,
    },
  } satisfies Record<string, ApplicationLink>;

  const bottomLinks = {
    contact: {
      text: t(RESOURCES.Contact),
      url: appUrl("contact"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <GmailLogoIcon />,
    },
    generalHelp: {
      text: t(RESOURCES.Help),
      url: "https://mo.one/napoveda",
      isExternalLink: true,
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <AskQuestionIcon />,
    },
    logout: {
      text: t(Resources.Menu.Logout),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <LogoutRoundedIcon />,
      isChevronHidden: true,
      onClick: () => {
        dispatch(
          resetUser({
            onAfterReset: () => navigate(appUrl("sign-in"), { replace: true }),
          }),
        );
      },
    },
  } satisfies Record<string, ApplicationLink>;

  const settingsLinks = {
    profile: {
      text: t(RESOURCES.UserAccount),
      url: appUrl("user-account"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <UserIcon />,
    },
    languageSettings: {
      text: t(RESOURCES.LanguageSettings),
      url: appUrl("language-settings"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <LanguageIcon />,
    },
    connectedApps: {
      text: t(RESOURCES.ConnectedApps),
      url: appUrl("connected-apps"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <AppsIcon />,
    },
    betaInfo: {
      text: t(Resources.Beta.MenuItem),
      url: appUrl("beta"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <ScienceOutlinedIcon />,
    },
  } satisfies Record<string, ApplicationLink>;

  return { ...topLinks, ...bottomLinks, ...settingsLinks };
};
